import { CalendarState } from '../../controller';
import { MemoizedViewModalFactory } from '../viewModel';
import { ViewModelFactoryParams } from '../../../../utils/ControlledComponent/ControlledComponent.types';
import { CalendarContext } from '../../../../utils/context/contextFactory';
import { formatRfcTimeStringToTimeSlotView } from '../../../../utils/dateAndTime/dateAndTime';
import {
  getServicePriceText,
  isOfferedAsOneTime,
} from '../../../../utils/payment/payment';
import { getSlotDuration } from '../../../../utils/duration/duration';
import { getLocationText } from '../../../../utils/bookingPreferences/bookingPreferences';
import { Optional } from '../../../../types/types';
import { getFormattedPrice } from '../../../../utils/dynamicPricing/dynamicPricing';
import {
  getStaffList,
  isFullSlot,
  isSlotLocked,
  isSlotWithBookingsPolicyViolation,
  isTooEarlyToBookSlot,
  isTooLateToBookSlot,
} from '../../../../utils/slotAvailability/slotAvailability';
import {
  createCtaViewModel,
  CtaViewModel,
  memoizedCtaViewModel,
} from '../ctaViewModel/ctaViewModel';
import { Service } from '@wix/ambassador-bookings-services-v2-service/types';
import { isDummyServices } from '../../../../api/dummyData/dummyServices';
import { withoutDefaultFallback } from '../../settingsParams';
import {
  TimeSlot,
  Resource,
} from '@wix/ambassador-bookings-availability-v2-time-slot/types';
import { isServiceWithVariedPrice } from '@wix/bookings-calendar-catalog-viewer-mapper';

export type AgendaSlot = {
  id: number;
  time: string;
  timeWidthHolder: string;
  serviceName: string;
  serviceIndex: number;
  spotsLeft?: string;
  duration?: string;
  location?: string;
  staffMember?: string;
  price?: string;
  policyViolation?: string;
  ctaViewModel?: CtaViewModel;
};

export type AgendaSlotsViewModel = {
  agendaSlots: AgendaSlot[];
  numberOfSlotsToDisplay?: number;
  showAllText: string;
};
export type EnrichedSlotAvailability = TimeSlot & { id: number };

type AgendaSlotsViewModelParams = ViewModelFactoryParams<
  CalendarState,
  CalendarContext
> & {
  slots: EnrichedSlotAvailability[];
};

export const enrichSlotAvailability = (slotsAvailability?: TimeSlot[]) => {
  return (
    slotsAvailability?.map((slotAvailability, index) => ({
      ...slotAvailability,
      id: index,
    })) || []
  );
};

export const memoizedAgendaSlotsViewModel: MemoizedViewModalFactory<AgendaSlotsViewModel> =
  {
    dependencies: {
      settings: [
        'spotsLeftFormat',
        'spotsLeftVisibility',
        'slotDurationVisibility',
        'slotLocationVisibility',
        'slotPriceVisibility',
        'slotRegistrationStatusVisibility',
        'slotStaffMemberVisibility',
        'toEarlyToBookIndication',
        'toLateToBookIndication',
        'ctaVisibility',
        'noSpotsLeft',
        'maxTimeSlotsDisplayedPerDay',
        'limitTimeSlotsDisplay',
      ],
      state: ['servicesInView'],
      subDependencies: [memoizedCtaViewModel.dependencies],
    },
  };

export function createAgendaSlotsViewModel({
  state,
  context,
  slots,
}: AgendaSlotsViewModelParams): AgendaSlotsViewModel {
  const { servicesInView } = state;
  const { t, settingsParams, settings, languageSettings, getContent } = context;
  const dateRegionalSettingsLocale =
    languageSettings!.dateRegionalSettingsLocale!;

  const shouldLimitSlots = settings.get(settingsParams.limitTimeSlotsDisplay);
  const numberOfSlotsToDisplay = settings.get(
    settingsParams.maxTimeSlotsDisplayedPerDay,
  );
  const isLocationVisible = settings.get(settingsParams.slotLocationVisibility);
  const isDurationVisible = settings.get(settingsParams.slotDurationVisibility);
  const isPriceVisible = settings.get(settingsParams.slotPriceVisibility);
  const isCtaVisible = settings.get(settingsParams.ctaVisibility);
  const isStaffMemberVisible = settings.get(
    settingsParams.slotStaffMemberVisibility,
  );
  const isPolicyViolationVisible = settings.get(
    settingsParams.slotRegistrationStatusVisibility,
  );

  return {
    agendaSlots:
      slots?.map<AgendaSlot>((enrichedSlot) => {
        const serviceIndex = servicesInView.findIndex(
          (availableService) => availableService.id === enrichedSlot.serviceId,
        );
        const service = servicesInView[serviceIndex];

        const rfcStartTime = enrichedSlot.localStartDate!;
        const rfcEndTime = enrichedSlot.localEndDate!;

        const time = formatRfcTimeStringToTimeSlotView(
          rfcStartTime,
          dateRegionalSettingsLocale,
        );
        const timeWidthHolder = formatRfcTimeStringToTimeSlotView(
          '2023-01-01T22:48',
          dateRegionalSettingsLocale,
        );
        const serviceName = service?.name!;

        const location = isLocationVisible
          ? getLocationText(enrichedSlot.location, t)
          : undefined;

        const staffMember = getStaffList(enrichedSlot);
        const staffMemberName = isStaffMemberVisible
          ? staffMember?.[0]?.name
          : undefined;

        const shouldShowDummyDuration =
          context.flowAPI.environment.isEditor &&
          isDummyServices(servicesInView);
        const duration = isDurationVisible
          ? shouldShowDummyDuration
            ? t('dummy-content.service.duration')
            : getSlotDuration({
                dateRegionalSettingsLocale,
                rfcStartTime,
                rfcEndTime,
                t,
              }).durationText
          : undefined;

        const price = isPriceVisible
          ? getSlotPrice({
              service: service!,
              staffMember: staffMember?.[0]!,
              context,
              state,
            })
          : undefined;

        const spotsLeft = getSpotsLeft({ slot: enrichedSlot, context });

        const policyViolation = isPolicyViolationVisible
          ? getPolicyViolationText({ context, slot: enrichedSlot })
          : undefined;

        const ctaViewModel = isCtaVisible
          ? createCtaViewModel({ state, context, slots: [enrichedSlot] })
          : undefined;

        return {
          id: enrichedSlot.id,
          time,
          timeWidthHolder,
          serviceName,
          location,
          spotsLeft,
          duration,
          price,
          staffMember: staffMemberName!,
          ctaViewModel,
          policyViolation,
          serviceIndex,
        };
      }) || [],
    showAllText: getContent({
      settingsParam: settingsParams.loadMoreTimeSlots,
      translationKey: 'app.settings.defaults.time-picker.daily-agenda.show-all',
    }),
    numberOfSlotsToDisplay: shouldLimitSlots
      ? numberOfSlotsToDisplay
      : undefined,
  };
}

const getSpotsLeft = ({
  slot,
  context,
}: {
  slot: EnrichedSlotAvailability;
  context: CalendarContext;
}): AgendaSlot['spotsLeft'] => {
  const { t, settingsParams, settings, getContent } = context;

  const openSpots = slot.bookableCapacity;
  const isSpotsLeftVisible = settings.get(settingsParams.spotsLeftVisibility);

  if (!isSpotsLeftVisible || isSlotWithBookingsPolicyViolation(slot)) {
    return;
  }

  if (isSlotLocked(slot)) {
    return t('app.slot.open-for-waitlist');
  }

  if (isFullSlot(slot)) {
    return getContent({
      settingsParam: settingsParams.noSpotsLeft,
      translationKey: 'app.settings.defaults.slots.no-spots-left',
    });
  }

  const customFormat = settings.get(
    withoutDefaultFallback(settingsParams.spotsLeftFormat),
  );
  if (customFormat) {
    return t('app.agenda-slot.spots-left.custom-label', {
      openSpots,
      spotsLeftFormat: customFormat,
    });
  }

  return t('app.agenda-slot.spots-left.label', {
    openSpots,
  });
};

const getSlotPrice = ({
  staffMember,
  context,
  state,
  service,
}: {
  staffMember: Resource;
  context: CalendarContext;
  state: CalendarState;
  service: Service;
}): Optional<string> => {
  const payment = service.payment!;

  const serviceVariants = state.serviceVariantsMap?.[service.id!];
  const regionalSettingsLocale =
    context.languageSettings.regionalSettingsLocale!;

  if (isOfferedAsOneTime(payment)) {
    if (isServiceWithVariedPrice(service)) {
      return getFormattedPrice({
        state,
        context,
        service,
        choiceId: staffMember.id!,
        optionId: serviceVariants?.options?.values?.[0].id!,
      });
    }

    return getServicePriceText(payment, regionalSettingsLocale, context.t);
  }
};

const getPolicyViolationText = ({
  context,
  slot,
}: {
  context: CalendarContext;
  slot: EnrichedSlotAvailability;
}): string | undefined => {
  const { getContent, settingsParams } = context;

  if (isTooEarlyToBookSlot(slot)) {
    return getContent({
      settingsParam: settingsParams.toEarlyToBookIndication,
      translationKey: 'app.settings.defaults.policy.to-early-to-book',
    });
  }

  if (isTooLateToBookSlot(slot)) {
    return getContent({
      settingsParam: settingsParams.toLateToBookIndication,
      translationKey: 'app.settings.defaults.policy.to-late-to-book',
    });
  }
};
